/**
 * LiveKit-Anbindung des Panel-PWA.
 *
 * Routing-Modell ("Wege"):
 *  - Jedes Panel publisht GENAU EINEN gemeinsamen Mic-Track `panel-mic:${panelId}`,
 *    sobald es irgendeinen Sende-Button (Intercom / Partyline / Call-to-All)
 *    aktiv hält. Mehrere gleichzeitig aktive Buttons teilen sich diesen Track.
 *  - WOHIN gesendet wird, steht NICHT im Track, sondern in den Participant-
 *    Metadaten unter `routes`:
 *        routes = { panels: string[], partylines: string[], all: boolean }
 *  - Empfänger entscheidet selbst (autoSubscribe:false), ob er einen Sender
 *    abonniert: nur wenn er Ziel ist (Panel-Adresse, Partyline, Call-to-All)
 *    ODER ihn aktiv mithört (Listen-Button). Dadurch hört man NUR die für
 *    einen bestimmten Wege – und nicht mehr jeden, der irgendwo drückt.
 *
 * Externe Geräte (Riedel etc.) laufen WEITERHIN über eigene, pro Button
 * publizierte Tracks `panel-mic:${panelId}:${buttonId}` + metadata.targets,
 * weil der Desktop-Mixer diese gezielt auf Hardware-Lautsprecher routet.
 *
 * Identity-Konvention: `panel-${panelId}`.
 */
import {
  Room,
  RoomEvent,
  Track,
  createLocalAudioTrack,
  type LocalAudioTrack,
  type RemoteAudioTrack,
  type RemoteParticipant,
  type RemoteTrackPublication,
} from 'livekit-client';
import type { ExternalDevice, PortKey } from '@livepuls/types';
import { parsePortKey } from '@livepuls/types';
import { api } from './api.js';
import { decideHear, buildRoutes, type PanelRoutes } from './routing.js';

interface TokenResponse {
  url: string;
  token: string;
  room: string;
  identity: string;
  /** STUN/TURN-Server (eigener coturn, per IP) für zuverlässiges ICE. */
  iceServers?: Array<{ urls: string | string[]; username?: string; credential?: string }>;
}

/** Baut die RTCIceServer-Liste (STUN + TURN) aus der Token-Response.
 *  TURN ist als Relay-Fallback wichtig, wenn der direkte UDP-Pfad scheitert. */
function toRtcIceServers(tok: TokenResponse): RTCIceServer[] {
  return (tok.iceServers ?? []).map((s) => ({
    urls: s.urls,
    ...(s.username !== undefined ? { username: s.username } : {}),
    ...(s.credential !== undefined ? { credential: s.credential } : {}),
  }));
}

interface SubscriptionHandle {
  audioEl: HTMLAudioElement;
  attachedTrack: RemoteAudioTrack | null;
  refs: number;
  volume: number;
  detachOff?: () => void;
  /**
   * Für Port-Subscriptions: WebAudio-Analyser auf dem MediaStream der
   * Remote-Audio-Spur. Erlaubt der UI, einen Pegel anzuzeigen. Für Device-
   * Engagements aktuell nicht gesetzt (Talk-back-Pegel ist nicht nötig).
   */
  audioCtx?: AudioContext;
  analyser?: AnalyserNode;
  level?: number;
  rafId?: number | null;
}

interface DeviceEngagement {
  buttonId: string;
  deviceId: string;
  micPublication?: Awaited<ReturnType<Room['localParticipant']['publishTrack']>>;
  micTrack?: LocalAudioTrack;
  subHandle?: SubscriptionHandle;
  spkPortKey?: PortKey;
  micPortKey?: PortKey;
}

interface PanelMetadata {
  kind: 'panel';
  panelId: string;
  routes: PanelRoutes;
  /** Geräte-Routing für den Desktop-Mixer (unverändertes Format). */
  targets: Array<{ buttonId: string; deviceId: string; spkPortKey?: PortKey }>;
}

let room: Room | null = null;
let connectPromise: Promise<Room> | null = null;
let currentPanelId: string | null = null;
/** Eindeutiges Suffix pro Browser-Session, damit zwei Verbindungen derselben
 *  Panel-ID sich nicht gegenseitig per DUPLICATE_IDENTITY rauswerfen. */
const SESSION_SUFFIX = Math.random().toString(36).slice(2, 8);

/** subscribe-handles per portKey (mehrere Buttons können dieselbe Quelle hören). */
const portSubs = new Map<PortKey, SubscriptionHandle>();
/** aktive Geräte-Engagements per buttonId. */
const deviceEngagements = new Map<string, DeviceEngagement>();

/* ---- Lokaler Sende-Zustand (was DIESES Panel gerade WOHIN sendet) ---- */
/** buttonId → Ziel-Panel (gerichteter Intercom). */
const talkIntercom = new Map<string, string>();
/** buttonId → Partyline-ID, in die gesprochen wird. */
const talkPartyline = new Map<string, string>();
/** buttonIds mit aktivem Call-to-All. */
const talkAll = new Set<string>();

/* ---- Lokaler Höre-Zustand (was DIESES Panel zusätzlich mithören will) ---- */
/** buttonId → Panel-ID, dessen Mic mitgehört wird (Listen / Partyline-Rückkanal). */
const listenPanel = new Map<string, string>();
/** buttonId → Partyline-ID, die mitgehört wird (Listen-Buttons). */
const listenPartyline = new Map<string, string>();
/** Partylines, in denen DIESES Panel Mitglied ist (= hat eine Port-Gruppen-
 *  Taste). Werden DAUERHAFT mitgehört — man hört die Gruppe immer, auch ohne
 *  die Taste zu drücken; die Taste steuert nur das Senden. */
const memberPartylines = new Set<string>();

/** Der eine gemeinsame ausgehende Mic-Track für Panel-Routing. */
let sharedMicTrack: LocalAudioTrack | null = null;
let sharedMicPub: Awaited<ReturnType<Room['localParticipant']['publishTrack']>> | null = null;
/** In-Flight-Sperre: verhindert, dass mehrere gleichzeitige Engage-Aufrufe
 *  den gemeinsamen Mic mehrfach publizieren (Race → Track-Flattern). */
let sharedMicPromise: Promise<void> | null = null;

/** Ob der Browser die Audio-Wiedergabe gerade erlaubt (Autoplay-Status). */
let audioPlaybackOk = true;
let onPlaybackChange: ((ok: boolean) => void) | null = null;

/** Letzter Mikrofon-/Sende-Fehler (für sichtbare Anzeige auf dem Panel). */
let lastMicError: string | null = null;
export function getMicError(): string | null {
  return lastMicError;
}

/** Incoming Panel-Audio-Elements per Sender-Identity. */
const intercomIncoming = new Map<string, HTMLAudioElement>();
/** Pro Quell-Panel gewünschte Hör-Lautstärke (0–100) für eingehendes
 *  Intercom-Audio. Wird vom passenden Button (Wisch-Lautstärke) gesetzt. */
const incomingVolume = new Map<string, number>();

/* ------------------------------------------------------------------
   iOS-taugliche Lautstärke über Web-Audio GainNodes.
   WICHTIG: iOS Safari IGNORIERT HTMLMediaElement.volume (read-only).
   Lautstärke lässt sich dort nur über einen GainNode in einem
   AudioContext regeln. Wir routen daher den MediaStream jedes
   Audio-Elements über einen GainNode an die Ausgabe und schalten das
   Element selbst stumm (nur Web-Audio ist hörbar). Schlägt das fehl,
   bleibt das Element unverändert hörbar (Fallback = volle Lautstärke).
   ------------------------------------------------------------------ */
let volCtx: AudioContext | null = null;
const elGain = new WeakMap<HTMLAudioElement, { src: MediaStreamAudioSourceNode; gain: GainNode }>();
// Alle aktiven GainNodes speichern – damit sie nicht gelöscht werden, wenn Elemente gelöscht werden!
const activeGains = new Set<GainNode>();

function getVolCtx(): AudioContext | null {
  try {
    if (!volCtx) {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      volCtx = new Ctor();
    }
    return volCtx;
  } catch {
    return null;
  }
}

/**
 * Baut – sofern der AudioContext läuft – eine GainNode-Route für das Element
 * auf (MediaStream → Gain → Ausgabe) und schaltet das Element stumm. Erst nach
 * unlockAudio() (Resume per Nutzergeste) ist der Context „running".
 */
function ensureGainRoute(el: HTMLAudioElement, vol01: number): boolean {
  const existing = elGain.get(el);
  if (existing) {
    existing.gain.gain.value = vol01;
    return true;
  }
  const ctx = getVolCtx();
  if (!ctx) return false;
  // Wenn suspended, versuche zu resumieren (z.B. auf iOS nach Nutzer-Geste)
  if (ctx.state === 'suspended') {
    void ctx.resume().catch(() => {});
  }
  // Warte nicht auf resume() – erstelle GainNode trotzdem, wird später aktiv
  const stream = el.srcObject as MediaStream | null;
  if (!stream || stream.getAudioTracks().length === 0) return false;
  try {
    const src = ctx.createMediaStreamSource(stream);
    const gain = ctx.createGain();
    gain.gain.value = vol01;
    src.connect(gain).connect(ctx.destination);
    elGain.set(el, { src, gain });
    activeGains.add(gain);  // ← In activeGains speichern!
    el.muted = true; // ab jetzt ist nur die Web-Audio-Kette hörbar
    return true;
  } catch (err) {
    console.warn('[livekit] gain route failed', err);
    return false;
  }
}

/** Alte Gain-Route lösen (z. B. wenn ein neuer Track an dasselbe Element kommt). */
function resetGainRoute(el: HTMLAudioElement): void {
  const r = elGain.get(el);
  if (!r) return;
  try {
    r.src.disconnect();
    r.gain.disconnect();
  } catch {
    /* noop */
  }
  activeGains.delete(r.gain);  // ← Aus activeGains entfernen!
  elGain.delete(el);
  // Element bleibt stumm – die Wiedergabe läuft ausschließlich über den Gain.
  el.muted = true;
}

/* ------------------------------------------------------------------
   Mic-Priority-Ducking: solange WIR senden, eingehendes Audio leiser
   regeln. Verhindert akustische Rückkopplung („super laut", wenn man
   zum Antworten den Sender öffnet während die Gegenstelle über den
   Lautsprecher läuft) und den iOS-Lautsprecher-Boost beim Mic-Start.
   ------------------------------------------------------------------ */
const DUCK_FACTOR = 0.3; // eingehend auf 30 %, während wir senden (70% Reduktion)
let transmitting = false;

/** Audio-Fader-Kurve (wie ein Mixer-Fader): oben bleibt es lange laut, leise
 *  wird es erst ganz unten. Konkav = Exponent < 1. Der angezeigte Wert (0–100)
 *  bleibt linear; nur der tatsächliche Pegel folgt dieser Kurve. */
const VOLUME_TAPER_EXP = 0.5;
function volumeTaper(p: number): number {
  return Math.pow(Math.max(0, Math.min(1, p)), VOLUME_TAPER_EXP);
}

/** Effektiver Gain (0–1) aus Soll-Lautstärke (0–100) inkl. Fader-Kurve + Ducking. */
function effGain(volume: number): number {
  const base = volumeTaper(volume / 100);
  return transmitting ? base * DUCK_FACTOR : base;
}

/** Alle eingehenden Gains mit aktuellem Duck-Faktor neu setzen. */
function reapplyDuckedGains(): void {
  console.log('[livekit] reapplyDuckedGains: transmitting=', transmitting, 'intercom:', intercomIncoming.size, 'portSubs:', portSubs.size, 'devices:', deviceEngagements.size, 'activeGains:', activeGains.size);
  for (const el of intercomIncoming.values()) {
    const vol = incomingVolume.get(el.dataset.panelId ?? '') ?? 100;
    const g = elGain.get(el);
    if (g) {
      g.gain.gain.value = effGain(vol);
      el.muted = true;  // Gain existiert → Element muten
    } else {
      el.muted = false; // Kein Gain → Element unmuten
      el.volume = effGain(vol);
    }
  }
  for (const h of portSubs.values()) {
    const g = elGain.get(h.audioEl);
    if (g) {
      g.gain.gain.value = effGain(h.volume);
      h.audioEl.muted = true;
    } else {
      h.audioEl.muted = false;
      h.audioEl.volume = effGain(h.volume);
    }
  }
  for (const e of deviceEngagements.values()) {
    if (e.subHandle) {
      const g = elGain.get(e.subHandle.audioEl);
      if (g) {
        g.gain.gain.value = effGain(e.subHandle.volume);
        e.subHandle.audioEl.muted = true;
      } else {
        e.subHandle.audioEl.muted = false;
        e.subHandle.audioEl.volume = effGain(e.subHandle.volume);
      }
    }
  }
  // WICHTIG: Auch alle activeGains aktualisieren, falls Elemente gelöscht wurden!
  // (Dies verhindert Race Conditions, wenn Elemente zwischen updateTransmitDucking und reapplyDuckedGains gelöscht werden)
  for (const gain of activeGains) {
    // Nutze den Standard-Gain-Wert (50%) wenn das Element nicht mehr in den Maps ist
    gain.gain.value = effGain(50);
  }
}

/** Sende-Zustand neu bewerten und Ducking anwenden. */
function updateTransmitDucking(): void {
  const on =
    talkIntercom.size > 0 ||
    talkPartyline.size > 0 ||
    talkAll.size > 0 ||
    Array.from(deviceEngagements.values()).some((e) => !!e.micTrack);
  if (on === transmitting) return;
  console.log('[livekit] updateTransmitDucking: transmitting', transmitting, '→', on, '{intercom:', talkIntercom.size, 'partyline:', talkPartyline.size, 'all:', talkAll.size, 'devices:', deviceEngagements.size, '}');
  transmitting = on;
  reapplyDuckedGains();
}

/** Lautstärke (0–100) auf ein Audio-Element anwenden – Gain ODER el.volume. */
function applyElementVolume(el: HTMLAudioElement, volume: number): void {
  const v = effGain(volume);
  const g = elGain.get(el);
  if (g) {
    // Gain existiert → nur Gain verwenden, Element MUTEN
    g.gain.gain.value = v;
    el.muted = true;
    return;
  }
  // Kein Gain: Versuche zu bauen
  if (ensureGainRoute(el, v)) {
    // Erfolgreich → Element bleibt muted (wurde in ensureGainRoute gesetzt)
    return;
  }
  // Fehler: Kein Gain möglich → Element unmuten und el.volume verwenden
  el.muted = false;
  el.volume = Math.max(0, Math.min(1, v));
}

function safeParseJson<T>(s: string | undefined | null): T | null {
  if (!s) return null;
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
}

/**
 * Liefert die Panel-ID, wenn `name` der GEMEINSAME Panel-Mic-Track ist
 * (`panel-mic:${panelId}`, exakt zwei Segmente). Gerätespezifische Tracks
 * `panel-mic:${panelId}:${buttonId}` (drei Segmente) liefern null – die sind
 * nicht für andere Panels bestimmt.
 */
function sharedMicPanelId(name: string | undefined): string | null {
  if (!name) return null;
  const parts = name.split(':');
  if (parts.length === 2 && parts[0] === 'panel-mic') return parts[1] ?? null;
  return null;
}

async function ensureConnected(panelId: string): Promise<Room> {
  if (room && currentPanelId === panelId) return room;
  if (connectPromise) return connectPromise;
  connectPromise = (async (): Promise<Room> => {
    // EINDEUTIGE Identität pro Browser-Session (Suffix). Sonst werfen sich
    // zwei Verbindungen mit derselben `panel-<id>`-Identität (mehrere Tabs,
    // alte Verbindung beim Reload, Geister-Panel) gegenseitig raus
    // (DUPLICATE_IDENTITY) → endlose Reconnect-Schleife → der Sende-Kanal
    // stabilisiert nie ("negotiation timed out"). Das Routing nutzt die
    // panelId aus den Metadaten/Track-Namen, nicht die Identität.
    const identity = `panel-${panelId}--${SESSION_SUFFIX}`;
    const tok = await api.post<TokenResponse>('/api/v1/livekit/token', {
      identity,
      name: `Panel ${panelId.slice(0, 8)}`,
      role: 'publisher-subscriber',
    });
    const r = new Room({
      adaptiveStream: false,
      dynacast: true,
    });
    r.on(RoomEvent.TrackSubscribed, onTrackSubscribed);
    r.on(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed);
    // Ein Sender hat zu senden begonnen oder sein Ziel geändert → neu bewerten,
    // ob wir ihn hören wollen.
    r.on(RoomEvent.TrackPublished, () => recomputeSubscriptions());
    r.on(RoomEvent.ParticipantMetadataChanged, () => recomputeSubscriptions());
    // Autoplay-Status der Wiedergabe beobachten (für die "Ton aktivieren"-UI).
    r.on(RoomEvent.AudioPlaybackStatusChanged, () => {
      audioPlaybackOk = r.canPlaybackAudio;
      onPlaybackChange?.(audioPlaybackOk);
    });
    r.on(RoomEvent.Disconnected, () => {
      console.warn('[livekit] disconnected');
      // NUR Handles verwerfen, ABSICHT (talk/listen) behalten → beim nächsten
      // Connect via reestablishAfterReconnect wieder wirksam machen. Sonst sind
      // gelatchte Partyline-/Intercom-Tasten nach einem Reconnect ohne Wirkung.
      teardownRuntimeHandles();
      room = null;
      currentPanelId = null;
      connectPromise = null;
    });
    // WICHTIG: STUN/TURN aus der Token-Response an die PeerConnection geben.
    // Ohne das fällt LiveKit auf Google-STUN (Hostname) zurück → in manchen
    // Netzen/Electron kein srflx, kein TURN-Relay → "negotiation timed out".
    // Unser coturn wird per IP adressiert (kein DNS) und liefert zusätzlich
    // ein TURN-Relay als Fallback hinter striktem NAT.
    const iceServers = toRtcIceServers(tok);
    await r.connect(tok.url, tok.token, {
      autoSubscribe: false,
      ...(iceServers.length > 0 ? { rtcConfig: { iceServers } } : {}),
    });
    room = r;
    currentPanelId = panelId;
    audioPlaybackOk = r.canPlaybackAudio;
    onPlaybackChange?.(audioPlaybackOk);
    // Schon laufende Sender bewerten (wir joinen, während andere sprechen).
    recomputeSubscriptions();
    // Nach einem Reconnect noch „gedrückte" Tasten wieder wirksam machen
    // (Sende-Seite: Mic + Routen). Beim allerersten Connect ist die Absicht
    // leer → harmlos.
    await reestablishAfterReconnect();
    return r;
  })().finally(() => {
    connectPromise = null;
  });
  return connectPromise;
}

/**
 * Zentrale Routing-Entscheidung: Soll dieses Panel den Sender `srcPanelId`
 * mit den angegebenen `routes` hören?
 */
function shouldHear(srcPanelId: string, routes: PanelRoutes): boolean {
  return decideHear({
    myPanelId: currentPanelId,
    srcPanelId,
    routes,
    listenPanels: Array.from(listenPanel.values()),
    // Aktive Listen-Buttons + Partylines, in denen wir Mitglied sind (dauerhaft).
    listenPartylines: [...listenPartyline.values(), ...memberPartylines],
  });
}

function findSharedMicPub(p: RemoteParticipant, srcPanelId: string): RemoteTrackPublication | null {
  const wanted = `panel-mic:${srcPanelId}`;
  for (const pub of p.trackPublications.values()) {
    if (pub.kind === Track.Kind.Audio && pub.trackName === wanted) {
      return pub as RemoteTrackPublication;
    }
  }
  return null;
}

/**
 * Bewertet ALLE remote Panel-Teilnehmer neu und abonniert / deabonniert deren
 * gemeinsamen Mic-Track entsprechend dem Routing. Wird bei jeder relevanten
 * Änderung gerufen (Metadata, Publish, lokale Listen-/Connect-Änderungen).
 */
function recomputeSubscriptions(): void {
  if (!room) return;
  // Pro panelId nur EINE Session hören (die zuletzt beigetretene). Verhindert
  // doppeltes Audio, wenn dasselbe Panel mehrfach verbunden ist (Reload,
  // zweiter Tab, Geister-Session) – dann publizieren zwei Teilnehmer denselben
  // `panel-mic:<panelId>`-Track.
  const chosen = new Map<string, string>(); // panelId → identity (neueste gewinnt)
  for (const p of room.remoteParticipants.values()) {
    const meta = safeParseJson<PanelMetadata>(p.metadata);
    if (!meta || meta.kind !== 'panel' || !meta.panelId) continue;
    chosen.set(meta.panelId, p.identity);
  }
  for (const p of room.remoteParticipants.values()) {
    const meta = safeParseJson<PanelMetadata>(p.metadata);
    if (!meta || meta.kind !== 'panel' || !meta.panelId) continue;
    const routes: PanelRoutes = meta.routes ?? { panels: [], partylines: [], all: false };
    const pub = findSharedMicPub(p, meta.panelId);
    if (!pub) continue; // sendet (noch) nicht
    const isChosen = chosen.get(meta.panelId) === p.identity;
    pub.setSubscribed(isChosen && shouldHear(meta.panelId, routes));
  }
}

function onTrackSubscribed(
  track: Track,
  publication: RemoteTrackPublication,
  participant: RemoteParticipant,
): void {
  if (track.kind !== Track.Kind.Audio) return;
  // Gemeinsamer Panel-Mic-Track → eigenes Audio-Element, sofort abspielen.
  const srcPanel = sharedMicPanelId(publication.trackName);
  if (srcPanel) {
    const key = participant.identity;
    // Dopplung vermeiden: läuft dieses Panel bereits über eine ANDERE Session,
    // diese zusätzliche Quelle gar nicht erst abspielen.
    for (const [k, ex] of intercomIncoming) {
      if (k !== key && ex.dataset.panelId === srcPanel) {
        publication.setSubscribed(false);
        return;
      }
    }
    let el = intercomIncoming.get(key);
    if (!el) {
      // Lautstärke aus dem zugehörigen Button (Standard 100 = voll).
      el = mountAudioElement(incomingVolume.get(srcPanel) ?? 100);
      el.dataset.panelId = srcPanel;
      intercomIncoming.set(key, el);
    }
    resetGainRoute(el); // evtl. alte Route (anderer/alter Stream) lösen
    (track as RemoteAudioTrack).attach(el);
    void el.play().catch(() => {});
    // iOS-taugliche Lautstärke: GainNode-Route (sofern Context schon läuft).
    applyElementVolume(el, incomingVolume.get(srcPanel) ?? 100);
    return;
  }
  const portKey: PortKey = `${participant.identity}::${publication.trackName}` as PortKey;
  const handle = portSubs.get(portKey);
  if (handle) attachToHandle(handle, track as RemoteAudioTrack);
  // außerdem: aktive Geräte, die diesen Port als ext-mic abonniert haben
  for (const eng of deviceEngagements.values()) {
    if (eng.subHandle && eng.micPortKey === portKey) {
      attachToHandle(eng.subHandle, track as RemoteAudioTrack);
    }
  }
}

function onTrackUnsubscribed(
  track: Track,
  publication: RemoteTrackPublication,
  participant: RemoteParticipant,
): void {
  if (track.kind !== Track.Kind.Audio) return;
  if (sharedMicPanelId(publication.trackName)) {
    const key = participant.identity;
    const el = intercomIncoming.get(key);
    if (el) {
      resetGainRoute(el);
      (track as RemoteAudioTrack).detach(el);
      el.remove();
      intercomIncoming.delete(key);
    }
    return;
  }
  const portKey: PortKey = `${participant.identity}::${publication.trackName}` as PortKey;
  const handle = portSubs.get(portKey);
  if (handle) detachFromHandle(handle);
  for (const eng of deviceEngagements.values()) {
    if (eng.subHandle && eng.micPortKey === portKey) detachFromHandle(eng.subHandle);
  }
}

/**
 * Erzeugt ein Audio-Element und hängt es (versteckt) ins DOM. WICHTIG für
 * mobile Browser (iOS/Android): off-DOM-Elemente werden dort wegen der
 * Autoplay-Policy oft NICHT abgespielt. Zusätzlich ist `playsInline` nötig,
 * sonst versucht iOS, in den Vollbild-Player zu springen.
 */
function mountAudioElement(_volume: number): HTMLAudioElement {
  const el = document.createElement('audio');
  el.autoplay = true;
  (el as HTMLAudioElement & { playsInline: boolean }).playsInline = true;
  el.setAttribute('playsinline', '');
  // Das Element selbst ist IMMER stumm: die Wiedergabe (inkl. Lautstärke) läuft
  // ausschließlich über die Web-Audio-GainNode-Route. Sonst spielt das Element
  // auf iOS unkontrollierbar in voller Lautstärke MIT → man hört doppelt
  // (Element voll + Gain). Das stumme, abspielende Element hält nur den
  // MediaStream am Leben, damit Web-Audio ihn überhaupt bekommt.
  el.muted = true;
  el.style.display = 'none';
  document.body.appendChild(el);
  return el;
}

function makeAudioElement(volume: number): HTMLAudioElement {
  return mountAudioElement(volume);
}

function attachToHandle(handle: SubscriptionHandle, track: RemoteAudioTrack): void {
  if (handle.attachedTrack === track) return;
  if (handle.attachedTrack) handle.attachedTrack.detach(handle.audioEl);
  resetGainRoute(handle.audioEl); // alte Route lösen, bevor neuer Stream kommt
  track.attach(handle.audioEl);
  applyElementVolume(handle.audioEl, handle.volume);
  handle.attachedTrack = track;
  startLevelMonitor(handle, track);
}

function detachFromHandle(handle: SubscriptionHandle): void {
  stopLevelMonitor(handle);
  resetGainRoute(handle.audioEl);
  if (handle.attachedTrack) {
    handle.attachedTrack.detach(handle.audioEl);
    handle.attachedTrack = null;
  }
}

/**
 * Hängt einen AnalyserNode an den MediaStream des Remote-Tracks und
 * schreibt den aktuellen Peak (0..1) nach handle.level. UI-Hooks lesen das
 * via readPortLevel(). Nicht-fatal bei Fehlern (z. B. wenn AudioContext
 * vom Browser blockiert wurde).
 */
function startLevelMonitor(handle: SubscriptionHandle, track: RemoteAudioTrack): void {
  stopLevelMonitor(handle);
  try {
    const stream = new MediaStream([track.mediaStreamTrack]);
    const ctx = new AudioContext();
    const src = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    src.connect(analyser);
    const buf = new Float32Array(analyser.fftSize);
    handle.audioCtx = ctx;
    handle.analyser = analyser;
    handle.level = 0;
    const tick = (): void => {
      if (!handle.analyser) return;
      handle.analyser.getFloatTimeDomainData(buf);
      let peak = 0;
      for (const v of buf) {
        const a = Math.abs(v);
        if (a > peak) peak = a;
      }
      // leichtes Smoothing (Pegel decay)
      handle.level = Math.max(peak, (handle.level ?? 0) * 0.85);
      handle.rafId = requestAnimationFrame(tick);
    };
    handle.rafId = requestAnimationFrame(tick);
  } catch (err) {
    console.warn('[livekit] level monitor failed', err);
  }
}

function stopLevelMonitor(handle: SubscriptionHandle): void {
  if (handle.rafId !== undefined && handle.rafId !== null) {
    cancelAnimationFrame(handle.rafId);
  }
  handle.rafId = null;
  if (handle.audioCtx) {
    void handle.audioCtx.close().catch(() => {});
  }
  delete handle.audioCtx;
  delete handle.analyser;
  handle.level = 0;
}

/** Aktueller Peak-Pegel für eine abonnierte ext-port Quelle (0..1). */
export function readPortLevel(portKey: PortKey): number {
  return portSubs.get(portKey)?.level ?? 0;
}

function findRemoteTrackForPort(r: Room, portKey: PortKey): RemoteTrackPublication | null {
  const parsed = parsePortKey(portKey);
  if (!parsed) return null;
  for (const p of r.remoteParticipants.values()) {
    if (p.identity !== parsed.identity) continue;
    for (const pub of p.trackPublications.values()) {
      if (pub.trackName === `${parsed.kind}:${parsed.slotId}`) {
        return pub as RemoteTrackPublication;
      }
    }
  }
  return null;
}

async function setSubscribed(r: Room, portKey: PortKey, sub: boolean): Promise<void> {
  const pub = findRemoteTrackForPort(r, portKey);
  if (!pub) return; // wird beim TrackPublished später attached
  pub.setSubscribed(sub);
}

/* ============================================================
   Gemeinsamer Mic-Track (für Intercom / Partyline / Call-to-All)
   ============================================================ */

function isTalking(): boolean {
  return talkIntercom.size > 0 || talkPartyline.size > 0 || talkAll.size > 0;
}

async function ensureSharedMic(r: Room): Promise<void> {
  if (sharedMicTrack) return;
  // Läuft bereits ein Publish? Dann auf dasselbe warten statt ein zweites zu starten.
  if (sharedMicPromise) return sharedMicPromise;
  if (!currentPanelId) return;
  sharedMicPromise = (async (): Promise<void> => {
    try {
      // EINZIGE Mikrofon-Aufnahme der App (keine separate Pegel-Aufnahme mehr).
      // Auf iOS scheitern zwei parallele getUserMedia – daher GENAU eine.
      const local = await createLocalAudioTrack({
        echoCancellation: true,
        noiseSuppression: true,
      });
      const pub = await r.localParticipant.publishTrack(local as unknown as MediaStreamTrack, {
        name: `panel-mic:${currentPanelId}`,
        source: Track.Source.Microphone,
        dtx: true,
        // KEIN red:true – RED (Audio-Redundanz) wird von iOS/WebKit beim
        // Publizieren oft nicht ausgehandelt → "negotiation timed out".
        red: false,
      });
      // Falls in der Zwischenzeit alle Sprech-Buttons losgelassen wurden,
      // den gerade fertig publizierten Track sofort wieder entfernen.
      if (!isTalking()) {
        try {
          r.localParticipant.unpublishTrack(local as unknown as MediaStreamTrack, true);
        } catch { /* noop */ }
        return;
      }
      sharedMicTrack = local;
      sharedMicPub = pub;
      lastMicError = null;
    } catch (err) {
      const e = err as Error;
      lastMicError = (e?.name || 'Error') + ': ' + (e?.message || String(err));
      console.warn('[livekit] shared mic publish failed', err);
    } finally {
      sharedMicPromise = null;
    }
  })();
  return sharedMicPromise;
}

async function maybeStopSharedMic(): Promise<void> {
  if (isTalking()) return;
  if (sharedMicPub && room) {
    try {
      room.localParticipant.unpublishTrack(
        (sharedMicPub.track ?? sharedMicTrack!) as unknown as MediaStreamTrack,
        true,
      );
    } catch (err) {
      console.warn('[livekit] shared mic unpublish failed', err);
    }
  }
  sharedMicTrack = null;
  sharedMicPub = null;
}

/* ============================================================
   Public API – externe Ports
   ============================================================ */

export async function subscribeExternalPort(opts: {
  panelId: string;
  portKey: PortKey;
  volume: number;
}): Promise<void> {
  const r = await ensureConnected(opts.panelId);
  let handle = portSubs.get(opts.portKey);
  if (!handle) {
    handle = {
      audioEl: makeAudioElement(opts.volume),
      attachedTrack: null,
      refs: 0,
      volume: opts.volume,
    };
    portSubs.set(opts.portKey, handle);
  }
  handle.refs += 1;
  handle.volume = opts.volume;
  applyElementVolume(handle.audioEl, opts.volume);
  await setSubscribed(r, opts.portKey, true);
}

export async function unsubscribeExternalPort(portKey: PortKey): Promise<void> {
  const handle = portSubs.get(portKey);
  if (!handle) return;
  handle.refs -= 1;
  if (handle.refs > 0) return;
  detachFromHandle(handle);
  portSubs.delete(portKey);
  if (room) await setSubscribed(room, portKey, false);
}

/* ============================================================
   Public API – externe Geräte (Desktop-Mixer-Routing, unverändert)
   ============================================================ */

export async function engageExternalDevice(opts: {
  panelId: string;
  buttonId: string;
  device: ExternalDevice;
  volume: number;
}): Promise<void> {
  const r = await ensureConnected(opts.panelId);
  if (deviceEngagements.has(opts.buttonId)) return;
  const eng: DeviceEngagement = {
    buttonId: opts.buttonId,
    deviceId: opts.device.id,
  };
  if (opts.device.micPortKey) eng.micPortKey = opts.device.micPortKey;
  if (opts.device.spkPortKey) eng.spkPortKey = opts.device.spkPortKey;
  deviceEngagements.set(opts.buttonId, eng);

  // Subscribe ext-mic des Geräts (Talk-back vom Gerät zum Panel)
  if (eng.micPortKey) {
    const handle: SubscriptionHandle = {
      audioEl: makeAudioElement(opts.volume),
      attachedTrack: null,
      refs: 1,
      volume: opts.volume,
    };
    eng.subHandle = handle;
    await setSubscribed(r, eng.micPortKey, true);
    const pub = findRemoteTrackForPort(r, eng.micPortKey);
    if (pub && pub.track && pub.track.kind === Track.Kind.Audio) {
      attachToHandle(handle, pub.track as RemoteAudioTrack);
    }
  }

  // Publish eigenen Mic-Track Richtung Gerät. Track-Name kodiert das Ziel-Gerät
  // (`panel-mic:${panelId}:${buttonId}`), damit der Desktop-Mixer ihn gezielt
  // auf den ext-spk Slot routen kann.
  const trackName = `panel-mic:${opts.panelId}:${opts.buttonId}`;
  try {
    const local = await createLocalAudioTrack({
      echoCancellation: true,
      noiseSuppression: true,
    });
    const pub = await r.localParticipant.publishTrack(local as unknown as MediaStreamTrack, {
      name: trackName,
      source: Track.Source.Microphone,
      dtx: true,
      red: false,
    });
    eng.micTrack = local;
    eng.micPublication = pub;
    updateTransmitDucking();
    await publishLocalMetadata();
  } catch (err) {
    console.warn('[livekit] publish mic failed', err);
  }
}

export async function disengageExternalDevice(buttonId: string): Promise<void> {
  const eng = deviceEngagements.get(buttonId);
  if (!eng) return;
  deviceEngagements.delete(buttonId);
  updateTransmitDucking();
  if (eng.subHandle) {
    detachFromHandle(eng.subHandle);
    if (room && eng.micPortKey) {
      const portStillUsed =
        portSubs.has(eng.micPortKey) ||
        Array.from(deviceEngagements.values()).some((e) => e.micPortKey === eng.micPortKey);
      if (!portStillUsed) await setSubscribed(room, eng.micPortKey, false);
    }
  }
  if (eng.micPublication && room) {
    try {
      room.localParticipant.unpublishTrack(
        (eng.micPublication.track ?? eng.micTrack!) as unknown as MediaStreamTrack,
        true,
      );
    } catch (err) {
      console.warn('[livekit] unpublish failed', err);
    }
  }
  await publishLocalMetadata();
}

/* ============================================================
   Public API – Panel-Routing (Intercom / Partyline / Call-to-All / Listen)
   ============================================================ */

/** Gerichteter Intercom: in dieses Ziel-Panel sprechen. */
export async function engageIntercom(opts: {
  panelId: string;
  buttonId: string;
  targetPanelId: string;
}): Promise<void> {
  const r = await ensureConnected(opts.panelId);
  talkIntercom.set(opts.buttonId, opts.targetPanelId);
  updateTransmitDucking();
  await ensureSharedMic(r);
  await publishLocalMetadata();
}

export async function disengageIntercom(buttonId: string): Promise<void> {
  if (!talkIntercom.delete(buttonId)) return;
  updateTransmitDucking();
  await maybeStopSharedMic();
  await publishLocalMetadata();
}

/** ext-mic-PortKeys der Geräte, die ein Partyline-Button gerade mithört. */
const partylineDeviceMics = new Map<string, string[]>();

/** Partyline: in die Line sprechen UND die Line mithören (Rückkanal).
 *  Optional gehören externe Geräte (bidi) zur Partyline: deren ext-mic wird
 *  mitgehört; ihr Lautsprecher hört die Partyline über den Desktop. */
export async function engagePartyline(opts: {
  panelId: string;
  buttonId: string;
  partylineId: string;
  /** ext-mic-PortKeys der Geräte-Mitglieder dieser Partyline. */
  deviceMicPortKeys?: string[];
}): Promise<void> {
  const r = await ensureConnected(opts.panelId);
  // Nur SENDEN. Das Mithören der Gruppe läuft dauerhaft über die Mitgliedschaft
  // (memberPartylines), nicht über die Taste.
  talkPartyline.set(opts.buttonId, opts.partylineId);
  updateTransmitDucking();
  await ensureSharedMic(r);
  // ext-mic der Geräte-Mitglieder mithören (kein eigenes Mic → iOS-sicher).
  const mics = (opts.deviceMicPortKeys ?? []).filter(Boolean);
  partylineDeviceMics.set(opts.buttonId, mics);
  for (const portKey of mics) {
    void subscribeExternalPort({ panelId: opts.panelId, portKey: portKey as PortKey, volume: 100 }).catch(
      (err) => console.warn('[livekit] partyline device mic subscribe failed', err),
    );
  }
  await publishLocalMetadata();
  recomputeSubscriptions();
}

export async function disengagePartyline(buttonId: string): Promise<void> {
  const had = talkPartyline.delete(buttonId);
  const mics = partylineDeviceMics.get(buttonId) ?? [];
  partylineDeviceMics.delete(buttonId);
  for (const portKey of mics) {
    void unsubscribeExternalPort(portKey as PortKey).catch((err) =>
      console.warn('[livekit] partyline device mic unsubscribe failed', err),
    );
  }
  updateTransmitDucking();
  if (!had) return;
  await maybeStopSharedMic();
  await publishLocalMetadata();
  recomputeSubscriptions();
}

/** Setzt die Partylines, in denen dieses Panel Mitglied ist (dauerhaftes
 *  Mithören). Wird von PanelView aus den Port-Gruppen-Tasten des Panels
 *  abgeleitet und bei jeder Config-Änderung aktualisiert. */
export function setMemberPartylines(ids: string[]): void {
  const next = new Set(ids);
  if (next.size === memberPartylines.size && [...next].every((id) => memberPartylines.has(id))) {
    return; // unverändert
  }
  memberPartylines.clear();
  for (const id of next) memberPartylines.add(id);
  recomputeSubscriptions();
}

/** Call-to-All: an alle Panels senden. */
export async function engageCallToAll(opts: {
  panelId: string;
  buttonId: string;
}): Promise<void> {
  const r = await ensureConnected(opts.panelId);
  talkAll.add(opts.buttonId);
  updateTransmitDucking();
  await ensureSharedMic(r);
  await publishLocalMetadata();
}

export async function disengageCallToAll(buttonId: string): Promise<void> {
  if (!talkAll.delete(buttonId)) return;
  updateTransmitDucking();
  await maybeStopSharedMic();
  await publishLocalMetadata();
}

/** Listen-Only: ein Panel ODER eine Partyline mithören (kein eigenes Senden). */
export async function engageListen(opts: {
  panelId: string;
  buttonId: string;
  targetPanelId?: string;
  partylineId?: string;
}): Promise<void> {
  const r = await ensureConnected(opts.panelId);
  if (opts.targetPanelId) {
    listenPanel.set(opts.buttonId, opts.targetPanelId);
    // „Beides": Ein Panel-Listen öffnet ZUSÄTZLICH einen gelatchten Sprech-
    // Rückkanal in das Ziel-Panel (damit man direkt zurücksprechen kann und das
    // Ziel sieht, dass wir reinhören). Endet automatisch mit dem Listen.
    talkIntercom.set(listenTalkKey(opts.buttonId), opts.targetPanelId);
    await ensureSharedMic(r);
  }
  if (opts.partylineId) listenPartyline.set(opts.buttonId, opts.partylineId);
  updateTransmitDucking();
  recomputeSubscriptions();
  await publishLocalMetadata();
}

export async function disengageListen(buttonId: string): Promise<void> {
  const a = listenPanel.delete(buttonId);
  const b = listenPartyline.delete(buttonId);
  const t = talkIntercom.delete(listenTalkKey(buttonId));
  if (t) updateTransmitDucking();
  if (a || b) recomputeSubscriptions();
  if (t) await maybeStopSharedMic();
  if (a || b || t) await publishLocalMetadata();
}

/** Schlüssel für den automatischen Sprech-Rückkanal eines Listen-Buttons. */
function listenTalkKey(buttonId: string): string {
  return `listen-talk:${buttonId}`;
}

/** Eager-Connect beim Mount der PanelView, damit Incoming-Tracks sofort
 *  empfangen werden, auch wenn dieses Panel selbst keinen Button drückt. */
export async function ensurePanelConnected(panelId: string): Promise<void> {
  try {
    await ensureConnected(panelId);
  } catch (err) {
    console.warn('[livekit] eager connect failed', err);
  }
}

/**
 * Entsperrt die Audio-Wiedergabe. MUSS aus einer echten Nutzergeste heraus
 * gerufen werden (z. B. pointerdown auf einem Button), sonst blockieren
 * mobile Browser / Safari das Abspielen der eingehenden Tracks komplett.
 *  - `room.startAudio()` hebt die Autoplay-Sperre für alle LiveKit-Elemente auf.
 *  - alle bereits gemounteten Audio-Elemente werden zusätzlich angestoßen.
 */
export function unlockAudio(): void {
  try {
    if (room) {
      void room
        .startAudio()
        .then(() => {
          audioPlaybackOk = room?.canPlaybackAudio ?? true;
          onPlaybackChange?.(audioPlaybackOk);
        })
        .catch(() => {});
    }
  } catch {
    /* noop */
  }
  for (const el of intercomIncoming.values()) void el.play().catch(() => {});
  for (const h of portSubs.values()) void h.audioEl.play().catch(() => {});
  for (const e of deviceEngagements.values()) {
    if (e.subHandle) void e.subHandle.audioEl.play().catch(() => {});
  }
  // Web-Audio-Context (für iOS-Lautstärke) aus der Nutzergeste heraus starten
  // und für alle bereits laufenden Elemente die GainNode-Route aufbauen.
  const ctx = getVolCtx();
  if (ctx) {
    void ctx
      .resume()
      .then(() => {
        for (const el of intercomIncoming.values()) {
          ensureGainRoute(el, effGain(incomingVolume.get(el.dataset.panelId ?? '') ?? 100));
        }
        for (const h of portSubs.values()) {
          ensureGainRoute(h.audioEl, effGain(h.volume));
        }
        for (const e of deviceEngagements.values()) {
          if (e.subHandle) {
            ensureGainRoute(e.subHandle.audioEl, effGain(e.subHandle.volume));
          }
        }
      })
      .catch(() => {});
  }
}

/** Registriert einen Callback, der bei Änderung des Autoplay-Status feuert. */
export function setAudioPlaybackListener(cb: ((ok: boolean) => void) | null): void {
  onPlaybackChange = cb;
  if (cb) cb(audioPlaybackOk);
}

/** Aktueller Autoplay-Status: true = Wiedergabe erlaubt. */
export function isAudioPlaybackOk(): boolean {
  return audioPlaybackOk;
}

/** Anzahl aktuell eingehender Tonquellen (Intercom + Ports) – für Debug-Anzeige. */
export function getIncomingAudioCount(): number {
  let n = 0;
  for (const el of intercomIncoming.values()) if (el.srcObject) n += 1;
  for (const h of portSubs.values()) if (h.attachedTrack) n += 1;
  return n;
}

/** Sende-Zustand für die Debug-Anzeige: Anzahl aktiver Sprech-Wege + ob der
 *  gemeinsame Mic-Track tatsächlich publiziert ist. */
export function getOutgoingState(): { ways: number; micLive: boolean } {
  return {
    ways: talkIntercom.size + talkPartyline.size + talkAll.size,
    micLive: !!sharedMicTrack,
  };
}

/**
 * Panel-IDs, von denen wir GERADE reinkommendes Intercom-Audio empfangen
 * (= die uns gerade ansprechen). Wird für das Orange-Leuchten der zugehörigen
 * Buttons genutzt. Identity-Konvention: `panel-${panelId}`.
 */
export function getActiveIncomingPanels(): string[] {
  if (!room) return [];
  const out: string[] = [];
  for (const [identity, el] of intercomIncoming.entries()) {
    if (!el.srcObject) continue;
    // panelId aus den Metadaten des Senders ableiten (nicht aus der Identität,
    // die jetzt ein Session-Suffix trägt).
    const p = room.remoteParticipants.get(identity);
    const meta = safeParseJson<PanelMetadata>(p?.metadata);
    if (meta?.panelId) out.push(meta.panelId);
  }
  return out;
}

export function setExternalPortVolume(portKey: PortKey, volume: number): void {
  const handle = portSubs.get(portKey);
  if (!handle) return;
  handle.volume = volume;
  applyElementVolume(handle.audioEl, volume);
}

export function setExternalDeviceVolume(buttonId: string, volume: number): void {
  const eng = deviceEngagements.get(buttonId);
  if (!eng?.subHandle) return;
  eng.subHandle.volume = volume;
  applyElementVolume(eng.subHandle.audioEl, volume);
}

/** Hör-Lautstärke für eingehendes Intercom-Audio eines Quell-Panels (0–100).
 *  So mischt sich jedes Panel sein eigenes Verhältnis zu allen Gegenstellen. */
export function setIncomingPanelVolume(panelId: string, volume: number): void {
  incomingVolume.set(panelId, volume);
  for (const el of intercomIncoming.values()) {
    if (el.dataset.panelId === panelId) applyElementVolume(el, volume);
  }
}

/**
 * Publisht den kompletten lokalen Routing-Zustand als Participant-Metadata:
 *  - `routes`  → Panel-Empfänger entscheiden damit, ob sie uns hören.
 *  - `targets` → Desktop-Mixer routet damit Geräte-Talk-Tracks auf Speaker.
 */
async function publishLocalMetadata(): Promise<void> {
  if (!room || !currentPanelId) return;
  const routes: PanelRoutes = buildRoutes({
    intercomTargets: talkIntercom.values(),
    partylineTargets: talkPartyline.values(),
    callToAll: talkAll.size > 0,
  });
  const targets = Array.from(deviceEngagements.values()).map((e) => ({
    buttonId: e.buttonId,
    deviceId: e.deviceId,
    ...(e.spkPortKey ? { spkPortKey: e.spkPortKey } : {}),
  }));
  const meta: PanelMetadata = { kind: 'panel', panelId: currentPanelId, routes, targets };
  try {
    await room.localParticipant.setMetadata(JSON.stringify(meta));
  } catch (err) {
    console.warn('[livekit] setMetadata failed', err);
  }
}

/** Verwirft NUR die LiveKit-Laufzeit-Handles (Tracks, GainNodes, Mic, Ports,
 *  Geräte). Die Panel-Routing-ABSICHT (talk-/listen-Maps) bleibt erhalten,
 *  damit sie nach einem Reconnect wiederhergestellt werden kann. */
function teardownRuntimeHandles(): void {
  for (const h of portSubs.values()) detachFromHandle(h);
  portSubs.clear();
  for (const e of deviceEngagements.values()) {
    if (e.subHandle) detachFromHandle(e.subHandle);
  }
  deviceEngagements.clear();
  for (const el of intercomIncoming.values()) {
    try {
      resetGainRoute(el);
      el.srcObject = null;
      el.remove();
    } catch {
      /* noop */
    }
  }
  intercomIncoming.clear();
  sharedMicTrack = null;
  sharedMicPub = null;
  sharedMicPromise = null;
}

/** Kompletter Reset INKL. Absicht. */
function teardownAllSubscriptions(): void {
  teardownRuntimeHandles();
  talkIntercom.clear();
  talkPartyline.clear();
  talkAll.clear();
  listenPanel.clear();
  listenPartyline.clear();
  memberPartylines.clear();
}

/** Nach einem Reconnect: die noch „gedrückten" Tasten wieder wirksam machen.
 *  Die HÖR-Seite deckt recomputeSubscriptions über die erhaltenen listen*-Maps
 *  ab; hier die SENDE-Seite (Mic + Routen-Broadcast), sonst wirkt die Taste,
 *  aber es kommt/geht kein Audio. */
async function reestablishAfterReconnect(): Promise<void> {
  if (!room) return;
  updateTransmitDucking();
  if (isTalking()) await ensureSharedMic(room);
  await publishLocalMetadata();
}
